<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { UserIcon } from "vue-tabler-icons";
import { useAuthStore } from "~/store/auth-store";
import { useRolePermission } from "~/composables/useRolePermission";
import { useUserStore } from "~/store/user-store";
import { useRoute, useRouter } from "vue-router";
import pageRoutes from "~/code/page-routes";
import ThemeSwitcher from "@/components/shared/ThemeSwitcher.vue";

const authStore = useAuthStore();
const userStore = useUserStore();
const {
  allUserRoles,
  isAdminUser,
  impersonatedRole,
  isImpersonating,
  setImpersonatedRole,
  computeRoles,
  computePermissions,
  computeRolesAndPermissions,
} = useRolePermission();
const route = useRoute();
const router = useRouter();
const avatar = ref("");

const normalizePath = (path?: string | null) => {
  return path ? path.replace(/\/+$/, "").toLowerCase() : "";
};

const enforceCurrentRouteAuthorization = () => {
  const normalizedPath = normalizePath(route.path);

  let currentRoute = pageRoutes.find(
    (item) => normalizePath(item.to) === normalizedPath
  );

  if (!currentRoute) {
    currentRoute = pageRoutes.find((item) => {
      return (
        item.subroutes &&
        Array.isArray(item.subroutes) &&
        item.subroutes.some(
          (subroute) => normalizePath(subroute) === normalizedPath
        )
      );
    });
  }

  if (!currentRoute) {
    return;
  }

  let isAuthorized = true;

  if (!currentRoute.roles && !currentRoute.permissions) {
    isAuthorized = true;
  } else if (!currentRoute.roles && currentRoute.permissions) {
    isAuthorized = computePermissions(currentRoute.permissions);
  } else if (currentRoute.roles && !currentRoute.permissions) {
    isAuthorized = computeRoles(currentRoute.roles);
  } else {
    isAuthorized = computeRolesAndPermissions(
      currentRoute.roles || [],
      currentRoute.permissions || []
    );
  }

  if (!isAuthorized && normalizePath(route.path) !== "/unauthorized") {
    router.push("/unauthorized");
  }
};

const selectedRole = computed({
  get: () => impersonatedRole.value || "",
  set: (value: string) => {
    const nextRole = value || null;
    const currentRole = impersonatedRole.value || null;

    if (nextRole === currentRole) {
      return;
    }

    setImpersonatedRole(nextRole);

    if (typeof window !== "undefined") {
      window.location.reload();
      return;
    }

    enforceCurrentRouteAuthorization();
  },
});

const roleOptions = computed(() => {
  const assignedRoles = allUserRoles.value;
  const managedRoles = userStore.roles
    .map((role) => role.name)
    .filter((role): role is string => Boolean(role && role.trim().length > 0));

  const sourceRoles = isAdminUser.value && managedRoles.length > 0
    ? managedRoles
    : assignedRoles;

  const uniqueRoles = Array.from(new Set(sourceRoles.map((role) => role.trim())));

  return uniqueRoles.map((role) => ({
    title: role,
    value: role,
  }));
});

onMounted(async () => {
  if (!isAdminUser.value || userStore.roles.length > 0) {
    return;
  }

  await userStore.fetchRoles(1, 500, "name", "asc", null);
});

const handleLogout = async () => {
  await authStore.logout();
  console.log("User logged out");
};

const getAvatar = (str: string) => {
  const canvas = document.createElement("canvas");
  canvas.width = 100;
  canvas.height = 100;
  const ctx = canvas.getContext("2d");

  const color = stringToColor(str);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(50, 50, 50, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "48px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(str.charAt(0).toUpperCase(), 50, 50);

  return canvas.toDataURL();
};

const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ("00" + value.toString(16)).substr(-2);
  }
  return color;
};

avatar.value = getAvatar(authStore.user?.value?.userName ?? "Unknown");
</script>

<template>
  <!-- ---------------------------------------------- -->
  <!-- notifications DD -->
  <!-- ---------------------------------------------- -->
  <v-menu :close-on-content-click="false">
    <template v-slot:activator="{ props }">
      <v-btn
        class="profileBtn custom-hover-primary"
        variant="text"
        v-bind="props"
        icon
      >
        <v-avatar size="35">
          <img :src="avatar" height="35" alt="user" />
        </v-avatar>
      </v-btn>
    </template>
    <v-sheet rounded="md" width="320" elevation="10" class="mt-2">
      <v-list class="py-0" lines="one" density="compact">
        <v-list-item value="item1" color="primary">
          <template v-slot:prepend>
            <UserIcon stroke-width="1.5" size="20" />
          </template>
          <v-list-item-title class="pl-4 text-body-1"
            >My Profile</v-list-item-title
          >
        </v-list-item>
      </v-list>

      <div class="px-5 pb-3">
        <div class="text-caption text-medium-emphasis mb-1">Roles</div>

        <div class="d-flex flex-wrap" style="gap: 6px">
          <v-chip
            v-for="role in allUserRoles"
            :key="role"
            size="small"
            :color="selectedRole === role ? 'primary' : undefined"
            :variant="selectedRole === role ? 'flat' : 'tonal'"
          >
            {{ role }}
          </v-chip>
        </div>
      </div>

      <div v-if="isAdminUser" class="px-5 pb-3">
        <v-select
          v-model="selectedRole"
          :items="roleOptions"
          item-title="title"
          item-value="value"
          label="Impersonate role"
          density="comfortable"
          clearable
          hide-details
        ></v-select>

        <div class="text-caption mt-1" :class="isImpersonating ? 'text-warning' : 'text-medium-emphasis'">
          <span v-if="isImpersonating">Role impersonation is active for UI authorization checks.</span>
          <span v-else>Using full admin roles.</span>
        </div>
      </div>

      <div class="py-0 px-5 text-center">
        <ThemeSwitcher />
      </div>
      <div class="pt-4 pb-4 px-5 text-center">
        <v-btn
          to="/auth"
          @click="handleLogout"
          color="primary"
          variant="outlined"
          block
          >Logout</v-btn
        >
      </div>
    </v-sheet>
  </v-menu>
</template>
